import React from 'react';
import { motion } from 'framer-motion';
import sectionsData from '../data/sections.json';

interface SocialLink {
  platform: string;
  url: string;
}

interface ContactInfo {
  email: string;
  preferredMethod: string;
  responseTime: string;
}

interface ContactMetadata {
  social: SocialLink[];
  contact: ContactInfo;
}

interface ContactProps {
  metadata?: ContactMetadata;
}

const Contact: React.FC<ContactProps> = ({ metadata }) => {
  // If metadata is not provided via props, get it from sections.json
  const contactData = React.useMemo(() => {
    if (metadata) return metadata;
    
    const contactSection = sectionsData.sections.find(
      (section: any) => section.id === 'contact'
    );
    console.log('Contact section data:', contactSection);
    
    return contactSection?.metadata as ContactMetadata;
  }, [metadata]);

  const { social, contact } = contactData || { social: [], contact: { email: '', preferredMethod: '', responseTime: '' } };
  console.log('Contact data being used:', { social, contact });

  const socialIcons: Record<string, JSX.Element> = {
    github: (
      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
        <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
      </svg>
    ),
    linkedin: (
      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
      </svg>
    ),
    twitter: (
      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
        <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
      </svg>
    ),
  };

  if (!contactData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-300">Loading contact information...</div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-full items-center justify-center p-2">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-black/40 backdrop-blur-sm rounded-lg p-8 align-middle items-center text-center"
      >
        <h2 className="text-3xl font-inter text-white mb-6">Let's Connect</h2>
        
        {/* Email and Response Time */}
        <div className="mb-8">
          <p className="text-white/80 mb-2">
            <span className="font-semibold">Email:</span> {contact.email}
          </p>
          <p className="text-white/60 text-sm">
            Preferred contact method: {contact.preferredMethod}
            <br />
            Response time: {contact.responseTime}
          </p>
        </div>

        {/* Social Links */}
        <div className="flex gap-6 align-middle items-center justify-center">
          {social.map((item) => (
            <motion.a
              key={item.platform}
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/70 hover:text-white transition-colors duration-300"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              {socialIcons[item.platform]}
            </motion.a>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default Contact; 